import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play, Pause, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Helper to detect if URL is a YouTube video
const isYouTubeUrl = (url) => {
  return url?.includes('youtube.com') || url?.includes('youtu.be');
};

// Get YouTube video ID
const getYouTubeVideoId = (url) => {
  let videoId = '';
  if (url.includes('youtube.com/watch')) {
    videoId = url.split('v=')[1]?.split('&')[0];
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  } else if (url.includes('youtube.com/embed/')) {
    videoId = url.split('embed/')[1]?.split('?')[0];
  }
  return videoId;
};

// Convert YouTube URL to embed URL with autoplay, mute, loop (for background)
const getYouTubeEmbedUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return url;
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
};

// Get full YouTube embed URL for popup (with sound, controls and autoplay)
const getYouTubePopupUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return url;
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
};

const HeroSlider = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [videoPopup, setVideoPopup] = useState(null); // Stores video URL for popup

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsTransitioning(false), 800);
  }, [slides.length, isTransitioning]);

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsTransitioning(false), 800);
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsTransitioning(false);
      setIsAutoPlaying(true);
    }, 5000);
  };

  useEffect(() => {
    if (!isAutoPlaying || slides.length <= 1 || videoPopup) return;
    
    // Check if current slide is a video - use longer interval
    const currentSlideData = slides[currentSlide];
    const isCurrentVideo = isYouTubeUrl(currentSlideData?.image_url) || currentSlideData?.video_url;
    const interval = isCurrentVideo ? 8000 : 5000; // 8 seconds for video, 5 for images
    
    const timer = setInterval(nextSlide, interval);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide, slides.length, currentSlide, slides, videoPopup]);

  if (!slides || slides.length === 0) return null;

  return (
    <section className="relative h-[60vh] sm:h-[70vh] min-h-[400px] max-h-[600px] overflow-hidden bg-slate-900" data-testid="hero-slider">
      {/* Background Slides */}
      {slides.map((slide, index) => {
        const isVideo = isYouTubeUrl(slide.image_url) || slide.video_url;
        const videoUrl = slide.video_url || slide.image_url;
        
        return (
          <div
            key={slide.id || index}
            className={`absolute inset-0 transition-all duration-1000 ease-out ${
              index === currentSlide 
                ? 'opacity-100 scale-100 z-10' 
                : 'opacity-0 scale-105 z-0'
            }`}
          >
            {/* Image or Video with Parallax Effect */}
            <div className="absolute inset-0 overflow-hidden">
              {isVideo && isYouTubeUrl(videoUrl) ? (
                <div className="absolute inset-0 w-full h-full overflow-hidden">
                  {/* YouTube Video Background - autoplay muted */}
                  {index === currentSlide && (
                    <iframe
                      src={getYouTubeEmbedUrl(videoUrl)}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                      style={{ 
                        width: '177.78vh', 
                        height: '100vh',
                        minWidth: '100%',
                        minHeight: '56.25vw'
                      }}
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      frameBorder="0"
                      title={slide.title}
                    />
                  )}
                  {/* Fallback Thumbnail */}
                  <img
                    src={`https://img.youtube.com/vi/${getYouTubeVideoId(videoUrl)}/maxresdefault.jpg`}
                    alt={slide.title}
                    className="absolute inset-0 w-full h-full object-cover -z-10"
                  />
                </div>
              ) : (
                <img
                  src={slide.image_url}
                  alt={slide.title}
                  className={`w-full h-full object-cover transition-transform duration-[8000ms] ease-out ${
                    index === currentSlide ? 'scale-110' : 'scale-100'
                  }`}
                />
              )}
            </div>
            
            {/* Gradient Overlays - Black, medium */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/75 via-slate-900/55 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent"></div>
            
            {/* Grid Texture */}
            <div className="absolute inset-0 bg-grid-dark opacity-25"></div>
          </div>
        );
      })}

      {/* Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 w-full">
          {slides.map((slide, index) => (
            <div
              key={slide.id || index}
              className={`transition-all duration-700 ${
                index === currentSlide 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8 absolute inset-0 pointer-events-none'
              }`}
            >
              <div className="max-w-3xl">
                {/* Badge */}
                <div 
                  className={`inline-flex items-center gap-2 glass-dark rounded-full px-3 py-1.5 sm:px-5 sm:py-2 mb-4 sm:mb-8 transition-all duration-700 delay-100 ${
                    index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                  <span className="text-slate-300 text-xs sm:text-sm font-medium tracking-wide">Fahrschule Rößner</span>
                </div>

                {/* Title */}
                <h1 
                  className={`text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-black text-white leading-tight sm:leading-[1.1] mb-3 sm:mb-6 transition-all duration-700 delay-200 ${
                    index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  {slide.title}
                </h1>

                {/* Subtitle */}
                {slide.subtitle && (
                  <p 
                    className={`text-sm sm:text-lg md:text-xl lg:text-2xl text-slate-300 mb-5 sm:mb-10 leading-relaxed max-w-2xl transition-all duration-700 delay-300 ${
                      index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                  >
                    {slide.subtitle}
                  </p>
                )}

                {/* CTA Button */}
                {slide.link_url && slide.link_text && (
                  <div 
                    className={`transition-all duration-700 delay-400 ${
                      index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                      {slide.link_url.startsWith('http') ? (
                        <form method="POST" target="_blank" action={slide.link_url}>
                          <Button 
                            type="submit"
                            size="lg" 
                            className="btn-modern h-11 sm:h-14 px-6 sm:px-10 text-sm sm:text-lg font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-2xl shadow-blue-500/30 btn-glow"
                          >
                            {slide.link_text}
                            <ChevronRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                          </Button>
                        </form>
                      ) : (
                        <Link to={slide.link_url}>
                          <Button 
                            size="lg" 
                            className="btn-modern h-11 sm:h-14 px-6 sm:px-10 text-sm sm:text-lg font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-2xl shadow-blue-500/30 btn-glow"
                          >
                            {slide.link_text}
                            <ChevronRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                          </Button>
                        </Link>
                      )}
                      <a href="tel:+496920326059">
                        <Button 
                          size="lg" 
                          variant="outline" 
                          className="h-11 sm:h-14 px-5 sm:px-8 text-sm sm:text-lg font-medium border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 rounded-full backdrop-blur-sm"
                        >
                          Anrufen
                        </Button>
                      </a>
                    </div>
                  </div>
                )}
                
                {/* Video Button - fixed right position, only show on video slides */}
                {(isYouTubeUrl(slide.image_url) || slide.video_url) && index === currentSlide && (
                  <div className="absolute right-4 sm:right-8 bottom-1/3 z-30 hidden sm:block">
                    <Button 
                      size="lg" 
                      className="h-14 px-8 text-lg font-medium bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50 rounded-full shadow-xl"
                      onClick={() => setVideoPopup(slide.video_url || slide.image_url)}
                      data-testid="video-play-btn"
                    >
                      <Play className="w-5 h-5 mr-2" fill="white" />
                      Video ansehen
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Slide Navigation - Bottom Right */}
      <div className="absolute bottom-4 sm:bottom-8 right-4 sm:right-8 z-30 flex items-center gap-2 sm:gap-4">
        {/* Progress Dots */}
        <div className="flex items-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-500 ${
                index === currentSlide 
                  ? 'w-8 h-2 bg-blue-500 rounded-full' 
                  : 'w-2 h-2 bg-white/30 hover:bg-white/50 rounded-full'
              }`}
              data-testid={`slider-dot-${index}`}
            />
          ))}
        </div>

        {/* Nav Buttons */}
        {slides.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={prevSlide}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all"
              data-testid="slider-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextSlide}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all"
              data-testid="slider-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20 hidden lg:flex flex-col items-center gap-2 animate-float">
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-white rounded-full animate-bounce"></div>
        </div>
        <span className="text-white/50 text-xs tracking-widest uppercase">Scrollen</span>
      </div>

      {/* Video Popup Modal */}
      {videoPopup && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setVideoPopup(null)}
          data-testid="video-popup"
        >
          <div 
            className="relative w-full max-w-5xl mx-4 aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={getYouTubePopupUrl(videoPopup)}
              className="w-full h-full rounded-xl shadow-2xl"
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
              frameBorder="0"
              title="Video"
            />
            <button
              onClick={() => setVideoPopup(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              data-testid="close-video-popup"
            >
              <X className="w-8 h-8" />
              <span className="sr-only">Schließen</span>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSlider;
